import { useEffect, useState } from 'react';

interface AnnualActivityPackage {
  giftMonths?: number;
  annualActivity?: { id: number; startTime: number; endTime: number } | null;
}

const MAX_TIMEOUT_MS = 2_147_483_647;

export function isAnnualActivityActive(
  priceZone: string | undefined,
  pkg: AnnualActivityPackage | undefined,
  now: number,
): boolean {
  if (priceZone !== 'CN' || (pkg?.giftMonths ?? 0) <= 0) {
    return false;
  }
  const activity = pkg?.annualActivity;
  if (!activity || !Number.isFinite(activity.id) || activity.id <= 0) {
    return false;
  }
  const validPeriod = Number.isFinite(activity.startTime) && Number.isFinite(activity.endTime);
  return validPeriod && now >= activity.startTime && now < activity.endTime;
}

export function getActiveAnnualDuration(
  priceZone: string | undefined,
  pkg: Pay.PackageV2 | undefined,
): Pay.DurationDescV2 | undefined {
  return pkg?.durationDesc.find(
    (duration) =>
      duration.durationNum >= 12 && isAnnualActivityActive(priceZone, duration, Date.now()),
  );
}

export function useAnnualActivity(
  priceZone: string | undefined,
  pkg: AnnualActivityPackage | undefined,
  onBoundary?: () => void,
): boolean {
  const [clockRevision, setClockRevision] = useState(0);
  const activity = pkg?.annualActivity;
  const startTime = activity?.startTime;
  const endTime = activity?.endTime;
  const giftMonths = pkg?.giftMonths;
  useEffect(() => {
    if (
      priceZone !== 'CN' ||
      !activity ||
      !(giftMonths && giftMonths > 0) ||
      !Number.isFinite(startTime) ||
      !Number.isFinite(endTime)
    ) {
      return;
    }
    const now = Date.now();
    const nextBoundary = now < activity.startTime ? activity.startTime : activity.endTime;
    if (nextBoundary <= now) {
      return;
    }
    // 浏览器长定时器有上限，活动起止时刻重新判断，切换套餐时清理旧定时器。
    const timer = setTimeout(() => {
      setClockRevision((revision) => revision + 1);
      if (Date.now() >= nextBoundary) {
        onBoundary?.();
      }
    }, Math.min(nextBoundary - now, MAX_TIMEOUT_MS));
    return () => clearTimeout(timer);
  }, [priceZone, activity, startTime, endTime, giftMonths, clockRevision, onBoundary]);
  return isAnnualActivityActive(priceZone, pkg, Date.now());
}
